// src/utils/periodValidation.js

/**
 * Convert a date to YYYY-MM-DD using LOCAL date parts (no UTC shift).
 * Accepts: "YYYY-MM-DD", "DD/MM/YYYY", ISO strings with time,
 * Date objects, and dayjs objects.
 */

export function normalizePeriodDate(value) {
  if (!value) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const fromDate = (d) =>
    Number.isNaN(d.getTime())
      ? ""
      : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  // dayjs object
  if (typeof value === "object" && typeof value.toDate === "function") {
    return fromDate(value.toDate());
  }

  // Date object
  if (value instanceof Date) {
    return fromDate(value);
  }

  if (typeof value === "string") {
    const text = value.trim();

    // Plain YYYY-MM-DD: use exactly as given
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      return text;
    }

    // DD/MM/YYYY
    const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

    if (match) {
      return `${match[3]}-${match[2]}-${match[1]}`;
    }

    // ISO with time
    if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
      return fromDate(new Date(text));
    }
  }

  return "";
}


/**
 * Format YYYY-MM-DD as DD/MM/YYYY.
 */
export function formatPeriodDate(value) {
  const date = normalizePeriodDate(value);

  if (!date) return "";

  const [year, month, day] = date.split("-");

  return `${day}/${month}/${year}`;
}

/**
 * Validate a document date against the selected financial year.
 * (Used by other screens; the booking form has its own check with grace days.)
 */
export function validatePeriodDateWithGrace({ value, startDate, endDate, fieldName }) {
  const documentDate = normalizePeriodDate(value);
  const start = normalizePeriodDate(startDate);
  const end = normalizePeriodDate(endDate);

  if (!documentDate || !start || !end) {
    return {
      valid: false,
      message: "Date or financial year information is missing.",
    };
  }

  if (documentDate < start || documentDate > end) {
    return {
      valid: false,
      message:
        `${fieldName} must be between ` +
        `${formatPeriodDate(start)} and ${formatPeriodDate(end)} ` +
        `for the selected financial year.`,
    };
  }

  return {
    valid: true,
    message: "",
  };
}


/**
 * Validate a document date strictly within the selected financial year.
 * No grace period before or after the financial year.
 */
export function validatePeriodDate({
  value,
  startDate,
  endDate,
  fieldName = "Date",
}) {
  const documentDate = normalizePeriodDate(value);
  const start = normalizePeriodDate(startDate);
  const end = normalizePeriodDate(endDate);

  if (!start || !end) {
    return {
      valid: false,
      message: "Selected financial year dates are not available.",
    };
  }

  if (start > end) {
    return {
      valid: false,
      message: "The selected financial year dates are invalid.",
    };
  }

  if (!documentDate || documentDate < start || documentDate > end) {
    return {
      valid: false,
      message: `Enter the ${fieldName} between ${formatPeriodDate(
        start,
      )} to ${formatPeriodDate(end)}`,
    };
  }

  return {
    valid: true,
    message: "",
  };
}
